/**
 * ResultsFilterBar Component
 *
 * Shared count + controls bar used across list pages (Search, Practices,
 * Notes, Discovery paths/threads, Insights). The left side shows a live count
 * and a noun; the right side holds the icon controls and, optionally, a
 * far-right primary action:
 *
 *   [Filter]  opens one sectioned menu containing every filter, the sort, and
 *             the "Starred only" toggle. Built on `.options-dropdown` via
 *             {@link renderMenuSections}.
 *   [Select]  a plain action that hands off to the section's select mode.
 *   [+ Label] a `variant: 'primary'` action — a light-bordered "+ label" pill
 *             at the far right, split off by a thin divider. This is the
 *             reusable "add" affordance (e.g. "+ New experiment"); it condenses
 *             to `shortLabel` on narrow viewports.
 *
 * The bar carries no text labels for the active values, so the left-hand count
 * noun (driven by per-option `itemLabel`) and the Filter button's `--active`
 * accent tint (plus its "(N applied)" name) are the only state signals. Keep
 * both wired.
 *
 * @example
 * const bar = new ResultsFilterBar({
 *   container: mountEl,
 *   itemLabel: { singular: 'session', plural: 'sessions' },
 *   count: 23,
 *   fixedItemLabel: { singular: 'Session', plural: 'Sessions' }, // optional: keeps noun fixed across filters
 *   filters: [
 *     {
 *       id: 'status',
 *       label: 'Status',          // menu section header
 *       defaultValue: 'active',
 *       neutralValue: 'active',   // optional; defaults to options[0].value
 *       options: [
 *         // Per-option `itemLabel` overrides the bar's top-level itemLabel
 *         // for the count display whenever this option is active.
 *         { value: 'active', label: 'Active', itemLabel: { singular: 'active concern', plural: 'active concerns' } },
 *         { value: 'archived', label: 'Archived', itemLabel: { singular: 'archived concern', plural: 'archived concerns' } }
 *       ]
 *     }
 *   ],
 *   sort: {
 *     defaultValue: 'newest',
 *     options: [
 *       { value: 'newest', label: 'Newest' },
 *       { value: 'oldest', label: 'Oldest' }
 *     ]
 *   },
 *   actions: [
 *     // `icon: 'star'` + `pressed` => rendered INSIDE the filter menu.
 *     { id: 'starred', label: 'Starred only', icon: 'star', pressed: false, onClick: () => { ... } },
 *     // an icon action => a standalone icon button after the Filter trigger.
 *     { id: 'select', label: 'Select', icon: 'select', onClick: () => { ... } },
 *     // `variant: 'primary'` => a "+ label" pill at the far right, past a
 *     // divider. `shortLabel` is the narrow-viewport label (falls back to
 *     // `label`).
 *     { id: 'add', label: 'New experiment', shortLabel: 'Experiment', variant: 'primary', onClick: () => { ... } }
 *   ],
 *   onChange: (state) => { ... }
 * });
 *
 * bar.setCount(filtered.length);
 * bar.destroy();
 */
import { renderMenuSections, keepMenuInViewport } from './optionsMenu.js';
import { Tooltip } from '../utils/Tooltip.js';

/** Internal id for the filter menu. Never caller-supplied — it is interpolated
 *  into a `[data-dropdown-id="…"]` selector, and a caller value (e.g. Search's
 *  composite `"type:id"` keys) would throw a querySelector SyntaxError. */
const FILTER_MENU_ID = '__filter';

/** Section header used for the sort group in every consumer. */
const SORT_SECTION_LABEL = 'Sort';

export class ResultsFilterBar {
  /**
   * @param {Object} config
   * @param {HTMLElement} config.container - Mount element (its innerHTML is replaced)
   * @param {{singular: string, plural: string}} config.itemLabel - Label for count display
   * @param {number} [config.count=0] - Initial count
   * @param {{singular: string, plural: string}} [config.fixedItemLabel] - Optional fixed label for count display. When provided, the count stays dynamic but ignores filter-specific item labels.
   * @param {Array<{id: string, label?: string, defaultValue: string, neutralValue?: string, options: Array<{value:string,label:string,itemLabel?:{singular:string,plural:string}}>}>} [config.filters] - Filter controls, one menu section each (in array order). `label` is the section header. `neutralValue` is the value considered "no filter applied" for the Filter button's active state; it defaults to the FIRST option's value — NOT `defaultValue`, which every consumer seeds from current state and which would therefore never register as dirty. An option may carry its own `itemLabel` to drive the count noun (first matching active option in `filters` order wins).
   * @param {{defaultValue: string, neutralValue?: string, options: Array<{value:string,label:string}>}} [config.sort] - Sort control, rendered as the last menu section before any toggles.
   * @param {Array<{id: string, label: string, onClick: Function, icon?: string, pressed?: boolean, variant?: string, shortLabel?: string, toggle?: boolean, plusPrefix?: boolean, visible?: boolean}>} [config.actions] - Actions, classified by shape. (1) MENU TOGGLE iff `icon === 'star'` and it owns a `pressed` property — renders inside the filter menu, excluded from the button row; use `setActionPressed(id, pressed)` for URL→bar sync. (2) PRIMARY iff `variant === 'primary'` — a light-bordered "+ label" pill rendered at the FAR right, past a thin divider; `shortLabel` is its narrow-viewport label (defaults to `label`); `plusPrefix: false` omits the hardcoded "+ " icon (default true); `visible: true` adds the `results-filter-bar__add--shown` modifier class so the pill actually renders (the base class is hidden globally). (3) STANDALONE TOGGLE iff `toggle === true` — a standalone icon button after the Filter trigger, WITH `aria-pressed` + an active class; state seeded from `pressed` and synced via `setActionPressed(id, pressed)`; deliberately excluded from `_activeFilterCount()`/`hasActiveFilters()`/`reset()` (those are menu-owned). (4) Otherwise a standalone icon button after the Filter trigger, with `aria-label` only and NO `aria-pressed` (Select is a mode switch, not a toggle).
   * @param {Function} [config.onChange] - Receives ({ filters: {id: value}, sort, count }) on user selection
   * @param {Function} [config.countFormatter] - Optional `(count) => string` override for the count element. When provided, its return value replaces the default `itemLabel` pluralization; `setCount(n)` re-invokes it. Default path (no formatter) is unchanged.
   */
  constructor(config) {
    this.container = config.container;
    this.itemLabel = config.itemLabel;
    this.count = config.count ?? 0;
    this.fixedItemLabel = config.fixedItemLabel || null;
    this.filterConfigs = Array.isArray(config.filters) ? config.filters : [];
    this.sortConfig = config.sort || null;
    this.actionConfigs = Array.isArray(config.actions) ? config.actions : [];
    this.onChange = config.onChange;
    /** @type {Function|null} Optional `(count) => string` override for the count element. */
    this.countFormatter = config.countFormatter || null;

    this.filterValues = {};
    for (const f of this.filterConfigs) {
      this.filterValues[f.id] = f.defaultValue;
    }
    this.sortValue = this.sortConfig ? this.sortConfig.defaultValue : null;

    // Pressed state for toggle actions (the "Starred only" menu row). Keyed by
    // action id; seeded from each action's optional `pressed` flag.
    this.actionPressed = {};
    for (const a of this.actionConfigs) {
      this.actionPressed[String(a.id)] = Boolean(a.pressed);
    }

    this.openMenuId = null;
    this.rootEl = null;
    /** Set by destroy(). Guards every post-destroy write: a consumer's
     *  onChange can synchronously re-render the page and construct a NEW bar
     *  into the same mount, so work queued after that point would paint a
     *  zombie bar over the live one. */
    this._destroyed = false;
    /** @type {Function|null} Disposes the current render's tooltips. */
    this._tooltipCleanup = null;
    this.boundOutsideClick = this._handleOutsideClick.bind(this);
    this.boundKeydown = this._handleKeydown.bind(this);

    this._render();
    this._attachEvents();
    document.addEventListener('click', this.boundOutsideClick);
    document.addEventListener('keydown', this.boundKeydown);
  }

  /**
   * Actions that belong inside the filter menu (the starred toggle), vs. the
   * standalone icon buttons. Classification is explicit so a mode switch like
   * Select never inherits toggle semantics.
   * @param {Object} action
   * @returns {boolean}
   */
  _isMenuToggle(action) {
    return action.icon === 'star' && Object.hasOwn(action, 'pressed');
  }

  /**
   * Whether an action is the far-right "+ label" primary pill (vs. a menu
   * toggle or a plain icon button). Explicit `variant` so a mode switch like
   * Select never inherits the add treatment.
   * @param {Object} action
   * @returns {boolean}
   */
  _isPrimaryAction(action) {
    return action.variant === 'primary';
  }

  /**
   * Whether an action is a standalone pressed-toggle button (e.g. "Needs
   * review only") — a real toggle rendered OUTSIDE the filter menu, unlike
   * the starred menu toggle. Explicit `toggle: true` so a mode switch like
   * Select never inherits pressed semantics.
   * @param {Object} action
   * @returns {boolean}
   */
  _isStandaloneToggle(action) {
    return action.toggle === true;
  }

  /** @returns {Array<Object>} Actions rendered inside the filter menu. */
  _menuToggleActions() {
    return this.actionConfigs.filter(a => this._isMenuToggle(a));
  }

  /** @returns {Array<Object>} Standalone icon buttons (not menu toggles, not standalone toggles, not primary). */
  _buttonActions() {
    return this.actionConfigs.filter(a =>
      !this._isMenuToggle(a) && !this._isPrimaryAction(a) && !this._isStandaloneToggle(a)
    );
  }

  /** @returns {Array<Object>} Standalone pressed-toggle buttons (outside the filter menu). */
  _standaloneToggleActions() {
    return this.actionConfigs.filter(a => this._isStandaloneToggle(a));
  }

  /** @returns {Array<Object>} Far-right "+ label" primary pills. */
  _primaryActions() {
    return this.actionConfigs.filter(a => this._isPrimaryAction(a));
  }

  /** @returns {boolean} Whether the bar owns any menu-able control. */
  _hasMenu() {
    return this.filterConfigs.length > 0 || !!this.sortConfig || this._menuToggleActions().length > 0;
  }

  _render() {
    // The tooltip element lives outside its trigger and is removed on the
    // trigger's `mouseleave`. Re-rendering while a button is hovered would
    // strand a visible tooltip, so dispose the previous render's first.
    this._tooltipCleanup?.();
    this._tooltipCleanup = null;

    // Re-rendering replaces the DOM the open menu lived in. Capture the open
    // id, rebuild, then restore it, so a PROGRAMMATIC re-render (setFilterValue
    // / setActionPressed on a URL round-trip) doesn't slam shut a menu the user
    // didn't touch. A user selection closes the menu explicitly first, so this
    // is a no-op there.
    const reopenId = this.openMenuId;
    this.openMenuId = null;
    this.container.innerHTML = this._buildHtml();
    this.rootEl = this.container.querySelector('.results-filter-bar');
    if (reopenId) this._openMenu(reopenId);
  }

  _buildHtml() {
    const controls = [];
    if (this._hasMenu()) controls.push(this._buildFilterHtml());
    for (const action of this._buttonActions()) {
      controls.push(this._buildActionHtml(action));
    }
    for (const action of this._standaloneToggleActions()) {
      controls.push(this._buildStandaloneToggleHtml(action));
    }
    // Primary "+ label" pills sit at the far right, split off from the icon
    // controls by a thin divider — but only when there's something to their
    // left to divide from.
    const primaryActions = this._primaryActions();
    if (primaryActions.length) {
      if (controls.length) {
        controls.push('<span class="results-filter-bar__divider" aria-hidden="true"></span>');
      }
      for (const action of primaryActions) {
        controls.push(this._buildPrimaryActionHtml(action));
      }
    }
    return `
      <div class="results-filter-bar">
        <div class="results-filter-bar__left">
          ${this._buildCountHtml()}
        </div>
        <div class="results-filter-bar__right">
          ${controls.join('')}
        </div>
      </div>
    `;
  }

  /**
   * The Filter trigger plus its anchored sectioned menu. The wrapper carries
   * `position: relative` (CSS) — without it the absolutely-positioned menu
   * escapes to the nearest positioned ancestor, which on /search is the whole
   * sticky bar.
   * @returns {string} HTML
   */
  _buildFilterHtml() {
    const applied = this._activeFilterCount();
    const activeClass = applied ? ' results-filter-bar__action--active' : '';
    // The accent tint is a colour-only cue; the name carries the same state in
    // text, for screen readers and for anyone who can't pick it out at a glance.
    const label = applied ? `Filter (${applied} applied)` : 'Filter';
    return `
      <div class="results-filter-bar__filter" data-dropdown-id="${FILTER_MENU_ID}">
        <button type="button"
                class="results-filter-bar__action results-filter-bar__action--square${activeClass}"
                data-action="rfb-toggle" aria-haspopup="menu" aria-expanded="false"
                aria-label="${label}" data-tooltip="${label}">
          <svg width="25" height="25" aria-hidden="true"><use href="#icon-filter"/></svg>
        </button>
        <div class="options-dropdown__menu options-dropdown__menu--hidden" role="menu">
          ${this._buildFilterMenuHtml()}
        </div>
      </div>
    `;
  }

  /**
   * Menu body: one group per filter (in config order), then Sort, then the
   * unheaded starred toggle.
   * @returns {string} HTML
   */
  _buildFilterMenuHtml() {
    const groups = this.filterConfigs.map(f => ({
      id: f.id,
      label: f.label || '',
      ariaLabel: f.label || f.id,
      items: f.options.map(o => ({
        value: o.value,
        label: o.label,
        selected: o.value === this.filterValues[f.id]
      }))
    }));

    if (this.sortConfig) {
      groups.push({
        id: '__sort',
        label: SORT_SECTION_LABEL,
        items: this.sortConfig.options.map(o => ({
          value: o.value,
          label: o.label,
          selected: o.value === this.sortValue
        }))
      });
    }

    for (const action of this._menuToggleActions()) {
      const pressed = Boolean(this.actionPressed[String(action.id)]);
      groups.push({
        id: '__action',
        ariaLabel: action.label,
        items: [{
          value: String(action.id),
          label: action.label,
          selected: pressed,
          icon: pressed ? 'star' : 'star-outline'
        }]
      });
    }

    // Reset lives at the foot of the menu, in its own group so it picks up the
    // same divider the other groups have. Shown only when something is applied:
    // it doubles as a signal, and a permanently-dead row would be noise.
    const resetHtml = this._isDirty()
      ? `<div class="options-dropdown__group" role="group" aria-label="Reset">`
        + `<button type="button" role="menuitem"`
        + ` class="options-dropdown__item options-dropdown__item--reset" data-action="rfb-reset">`
        + `<span class="options-dropdown__item-label">Reset filters</span></button></div>`
      : '';

    return renderMenuSections(groups) + resetHtml;
  }

  /**
   * A standalone action button (Select). No `aria-pressed` — these are not
   * toggles, and stamping it would announce a mode switch as a pressable
   * state to assistive tech.
   * @param {{id:string,label:string,icon?:string}} action
   * @returns {string} HTML
   */
  _buildActionHtml(action) {
    const id = this._escape(String(action.id));
    if (action.icon) {
      return `
        <button type="button" class="results-filter-bar__action results-filter-bar__action--square"
                data-action="rfb-action" data-rfb-action-id="${id}"
                aria-label="${this._escape(action.label)}" data-tooltip="${this._escape(action.label)}">
          <svg width="25" height="25" aria-hidden="true"><use href="#icon-${this._escape(String(action.icon))}"/></svg>
        </button>
      `;
    }
    return `
      <button type="button" class="filter-dropdown__trigger results-filter-bar__action"
              data-action="rfb-action" data-rfb-action-id="${id}">
        ${this._escape(action.label)}
      </button>
    `;
  }

  /**
   * A standalone pressed-toggle action (e.g. "Needs review only"). Same shape
   * as {@link _buildActionHtml} but WITH `aria-pressed` and an active class —
   * these ARE real toggles, unlike Select's mode switch.
   * @param {{id:string,label:string,icon?:string}} action
   * @returns {string} HTML
   */
  _buildStandaloneToggleHtml(action) {
    const id = this._escape(String(action.id));
    const pressed = Boolean(this.actionPressed[String(action.id)]);
    const activeClass = pressed ? ' results-filter-bar__action--active' : '';
    const label = this._escape(action.label);
    if (action.icon) {
      return `
        <button type="button" class="results-filter-bar__action results-filter-bar__action--square${activeClass}"
                data-action="rfb-action" data-rfb-action-id="${id}"
                aria-pressed="${pressed}" aria-label="${label}" data-tooltip="${label}">
          <svg width="25" height="25" aria-hidden="true"><use href="#icon-${this._escape(String(action.icon))}"/></svg>
        </button>
      `;
    }
    return `
      <button type="button" class="filter-dropdown__trigger results-filter-bar__action${activeClass}"
              data-action="rfb-action" data-rfb-action-id="${id}"
              aria-pressed="${pressed}" data-tooltip="${label}">
        ${label}
      </button>
    `;
  }

  /**
   * The far-right "+ label" primary pill. The full and short labels both render
   * (one hidden per breakpoint via CSS) and are `aria-hidden`; the button's own
   * `aria-label` carries the full label so screen readers announce it
   * consistently regardless of which text is visible.
   *
   * `plusPrefix: false` omits the hardcoded "+ " icon (default true, unchanged
   * rendering). `visible: true` adds the `results-filter-bar__add--shown`
   * modifier class — the base class is hidden globally
   * (`dropdowns.css:.results-filter-bar__add`), so a consumer must opt in to
   * actually show its primary button.
   * @param {{id:string,label:string,shortLabel?:string,plusPrefix?:boolean,visible?:boolean}} action
   * @returns {string} HTML
   */
  _buildPrimaryActionHtml(action) {
    const id = this._escape(String(action.id));
    const label = this._escape(action.label);
    const shortLabel = this._escape(action.shortLabel || action.label);
    const showPrefix = action.plusPrefix !== false;
    const shownClass = action.visible === true ? ' results-filter-bar__add--shown' : '';
    return `
      <button type="button" class="results-filter-bar__action results-filter-bar__add${shownClass}"
              data-action="rfb-action" data-rfb-action-id="${id}" aria-label="${label}">
        ${showPrefix ? '<span class="results-filter-bar__add-icon" aria-hidden="true">+</span>' : ''}
        <span class="results-filter-bar__add-label results-filter-bar__add-label--full" aria-hidden="true">${label}</span>
        <span class="results-filter-bar__add-label results-filter-bar__add-label--short" aria-hidden="true">${shortLabel}</span>
      </button>
    `;
  }

  _buildCountHtml() {
    return `
      <span class="results-filter-bar__count">
        ${this._buildCountInnerHtml()}
      </span>
    `;
  }

  _activeItemLabel() {
    if (this.fixedItemLabel) {
      return this.fixedItemLabel;
    }

    for (const f of this.filterConfigs) {
      const activeValue = this.filterValues[f.id];
      const activeOption = f.options.find(o => o.value === activeValue);
      if (activeOption?.itemLabel) {
        return activeOption.itemLabel;
      }
    }
    return this.itemLabel;
  }

  /**
   * Whether any menu-owned control is off its neutral value — drives the
   * Filter button's active dot.
   *
   * Compares against `neutralValue` (defaulting to the first option), NOT
   * `defaultValue`: every consumer seeds `defaultValue` from its CURRENT
   * state, so `defaultValue === currentValue` on every reconstruct and a
   * defaultValue-based check would never fire — leaving a filtered page
   * looking unfiltered, which is the one thing this indicator exists to
   * prevent. Covers filters, sort, AND the starred toggle.
   *
   * @returns {boolean}
   */
  _isDirty() {
    return this._activeFilterCount() > 0;
  }

  /**
   * How many menu-owned controls are off their neutral value. Drives both the
   * accent tint and the "(N applied)" suffix on the trigger's name.
   * @returns {number}
   */
  _activeFilterCount() {
    let n = 0;
    for (const f of this.filterConfigs) {
      if (this.filterValues[f.id] !== this._neutralFor(f)) n += 1;
    }
    if (this.sortConfig && this.sortValue !== this._neutralFor(this.sortConfig)) n += 1;
    return n + this._menuToggleActions().filter(a => this.actionPressed[String(a.id)]).length;
  }

  /**
   * The value a filter/sort group counts as "not applied".
   *
   * Defaults to the FIRST option — NOT `defaultValue`, which every consumer
   * seeds from current state and which would therefore never register as dirty.
   * Single-sourced here so {@link reset} and {@link _activeFilterCount} can
   * never disagree: whatever tints the trigger is exactly what reset clears.
   * @param {{neutralValue?: string, options?: Array<{value: string}>}} cfg
   * @returns {string|undefined}
   */
  _neutralFor(cfg) {
    return Object.hasOwn(cfg, 'neutralValue') ? cfg.neutralValue : cfg.options?.[0]?.value;
  }

  /**
   * Whether any membership-affecting control is applied — filters and the
   * starred toggle, but NOT sort (sorting reorders, it can never hide a row).
   *
   * Sections use this to decide whether an empty list is worth offering a reset
   * for: "no rows because you filtered them out" is recoverable, "no rows at
   * all" is not.
   * @returns {boolean}
   */
  hasActiveFilters() {
    for (const f of this.filterConfigs) {
      if (this.filterValues[f.id] !== this._neutralFor(f)) return true;
    }
    return this._menuToggleActions().some(a => this.actionPressed[String(a.id)]);
  }

  _attachEvents() {
    if (!this.rootEl) return;
    this.rootEl.addEventListener('click', this._handleRootClick);
    // Re-armed on every render (the buttons are new nodes each time).
    this._tooltipCleanup = Tooltip.setupAll(this.rootEl, '[data-tooltip]', { position: 'below' });
  }

  /**
   * Delegated click handling. Every branch stops propagation so the bar's own
   * controls never reach the section's `data-action` switch.
   *
   * Deliberately has NO catch-all `[data-action]` branch: the select-mode
   * toolbar (rendered by `renderSelectToolbar`, not by this component) emits
   * bare `data-action="select-*"` buttons that MUST bubble to the section.
   */
  _handleRootClick = (e) => {
    const trigger = e.target.closest('[data-action="rfb-toggle"]');
    if (trigger) {
      e.stopPropagation();
      const dropdownEl = trigger.closest('[data-dropdown-id]');
      const id = dropdownEl?.dataset.dropdownId;
      if (id) this._toggleMenu(id);
      return;
    }
    const actionBtn = e.target.closest('[data-action="rfb-action"]');
    if (actionBtn) {
      e.stopPropagation();
      const action = this.actionConfigs.find(a => String(a.id) === actionBtn.dataset.rfbActionId);
      if (action?.onClick) action.onClick();
      return;
    }
    const resetBtn = e.target.closest('[data-action="rfb-reset"]');
    if (resetBtn) {
      e.stopPropagation();
      // Close BEFORE resetting — reset() can re-render the page out from under
      // this handler, and a close afterwards would touch a detached menu.
      this._closeAllMenus();
      this.reset();
      return;
    }
    const opt = e.target.closest('[data-action="rfb-menu"]');
    if (opt) {
      e.stopPropagation();
      this._selectOption(opt.dataset.rfbFilter, opt.dataset.rfbValue);
    }
  }

  _toggleMenu(id) {
    if (this.openMenuId === id) {
      this._closeAllMenus();
      return;
    }
    this._closeAllMenus();
    this._openMenu(id);
  }

  /**
   * Show one menu. Note the polarity: `.options-dropdown__menu--hidden` HIDES
   * by class (the inverse of the old `.filter-dropdown__menu--open`), so the
   * class is REMOVED to open.
   * @param {string} id
   */
  _openMenu(id) {
    if (!this.rootEl) return;
    const dropdownEl = this.rootEl.querySelector(`[data-dropdown-id="${id}"]`);
    if (!dropdownEl) return;
    const menu = dropdownEl.querySelector('.options-dropdown__menu');
    const trigger = dropdownEl.querySelector('[data-action="rfb-toggle"]');
    if (menu) menu.classList.remove('options-dropdown__menu--hidden');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    // After the class is removed — the menu measures as 0×0 while hidden. A
    // primary "+ …" pill (e.g. /practices) pushes the Filter/Select triggers
    // toward the middle of the bar, so a right-anchored menu can overrun the
    // left edge of the screen at the mobile menu size.
    if (menu) keepMenuInViewport(menu);
    this.openMenuId = id;
  }

  _closeAllMenus() {
    if (!this.rootEl) {
      this.openMenuId = null;
      return;
    }
    this.rootEl.querySelectorAll('.options-dropdown__menu').forEach(m =>
      m.classList.add('options-dropdown__menu--hidden')
    );
    this.rootEl.querySelectorAll('[data-action="rfb-toggle"]').forEach(t =>
      t.setAttribute('aria-expanded', 'false')
    );
    this.openMenuId = null;
  }

  /**
   * Apply a menu selection. `id` is a filter id, `'__sort'`, or `'__action'`
   * (the starred toggle, whose `value` is the action id).
   * @param {string} id
   * @param {string} value
   */
  _selectOption(id, value) {
    // Close on ANY pick, including a re-pick of the current value. These are
    // single-select groups: in practice a user wants one value and expects the
    // menu to dismiss. It also makes behaviour uniform — /search and
    // /practices route changes through page.replace → setFilterValue, which
    // re-renders, so those surfaces could never have kept the menu open.
    this._closeAllMenus();

    if (id === '__action') {
      const action = this.actionConfigs.find(a => String(a.id) === value);
      // The toggle's own onClick owns the state flip + re-render (sections
      // drive it through the URL or setActionPressed), so don't double-apply.
      if (action?.onClick) action.onClick();
      return;
    }

    if (id === '__sort') {
      if (this.sortValue === value) return;
      this.sortValue = value;
    } else {
      if (!(id in this.filterValues) || this.filterValues[id] === value) return;
      this.filterValues[id] = value;
    }
    this._refresh();
    if (this.onChange) {
      this.onChange(this.getState());
    }
  }

  _refresh() {
    if (this._destroyed) return;
    this._render();
    this._attachEvents();
  }

  _handleOutsideClick(e) {
    if (this.openMenuId === null || !this.rootEl) return;
    const openDropdown = this.rootEl.querySelector(`[data-dropdown-id="${this.openMenuId}"]`);
    if (!openDropdown || !openDropdown.contains(e.target)) {
      this._closeAllMenus();
    }
  }

  /**
   * Escape closes the open menu and returns focus to its trigger, matching
   * OptionsDropdown's contract. Only swallows the event while a menu is
   * actually open, so Escape still reaches the right panel and modals.
   * @param {KeyboardEvent} e
   */
  _handleKeydown(e) {
    if (e.key !== 'Escape' || this.openMenuId === null || !this.rootEl) return;
    const trigger = this.rootEl.querySelector(
      `[data-dropdown-id="${this.openMenuId}"] [data-action="rfb-toggle"]`
    );
    e.preventDefault();
    e.stopPropagation();
    this._closeAllMenus();
    trigger?.focus();
  }

  /**
   * Get current bar state.
   * @returns {{filters: Object, sort: string|null, count: number}}
   */
  getState() {
    return {
      filters: { ...this.filterValues },
      sort: this.sortValue,
      actions: { ...this.actionPressed },
      count: this.count
    };
  }

  /**
   * Restore every menu-owned control to its neutral value and notify once.
   *
   * Resets exactly what tints the trigger — filters, sort, AND the starred
   * toggle — because the affordance that offers this is shown precisely when
   * that tint is lit. Clearing a subset would leave the trigger looking applied
   * immediately after the user reset it.
   *
   * The starred toggle is normally section-owned (its `onClick` does the flip),
   * so the pressed state rides along on `getState().actions` and consumers that
   * own a toggle must sync from it. That keeps this to ONE notification and one
   * re-render instead of one per control.
   *
   * Fires nothing when already neutral, so a no-op reset can't spuriously push
   * a URL or refetch.
   * @returns {*} Whatever the consumer's onChange returns (it may be async).
   */
  reset() {
    if (this._destroyed || !this._isDirty()) return undefined;

    for (const f of this.filterConfigs) {
      this.filterValues[f.id] = this._neutralFor(f);
    }
    if (this.sortConfig) this.sortValue = this._neutralFor(this.sortConfig);
    for (const a of this._menuToggleActions()) {
      this.actionPressed[String(a.id)] = false;
    }

    this._refresh();
    // NOTHING may run after this: onChange can synchronously re-render the
    // page and destroy this instance (see _destroyed).
    return this.onChange ? this.onChange(this.getState()) : undefined;
  }

  /**
   * Update the displayed count without triggering onChange.
   * @param {number} n
   */
  setCount(n) {
    if (this._destroyed) return;
    this.count = n;
    if (!this.rootEl) return;
    const labelEl = this.rootEl.querySelector('.results-filter-bar__count');
    if (labelEl) {
      labelEl.innerHTML = this._buildCountInnerHtml();
    }
  }

  _buildCountInnerHtml() {
    if (this.countFormatter) {
      return this._escape(this.countFormatter(this.count));
    }
    const itemLabel = this._activeItemLabel();
    const noun = this.count === 1 ? itemLabel.singular : itemLabel.plural;
    return `<span class="results-filter-bar__count-num">${this.count}</span> ${this._escape(noun)}`;
  }

  /**
   * Programmatically set a filter value (does not trigger onChange). Goes
   * through the normal re-render so the menu checkmark, `aria-checked`, and
   * the Filter button's active dot all follow — this is the URL→bar sync path.
   * @param {string} id
   * @param {string} value
   */
  setFilterValue(id, value) {
    if (!(id in this.filterValues)) return;
    this.filterValues[id] = value;
    this._refresh();
  }

  /**
   * Programmatically set a toggle action's pressed state for URL→bar sync
   * (does not trigger onChange). Re-renders rather than patching an
   * `aria-pressed` attribute: the starred toggle is a menu row now, so there
   * is no standalone button to patch, and the Filter dot must recompute.
   * @param {string} id - Action id
   * @param {boolean} pressed - Pressed state
   */
  setActionPressed(id, pressed) {
    const key = String(id);
    if (!(key in this.actionPressed)) return;
    this.actionPressed[key] = Boolean(pressed);
    if (!this.rootEl) return;
    this._refresh();
  }

  /**
   * Clean up listeners and DOM.
   */
  destroy() {
    document.removeEventListener('click', this.boundOutsideClick);
    document.removeEventListener('keydown', this.boundKeydown);
    this._tooltipCleanup?.();
    this._tooltipCleanup = null;
    if (this.container) this.container.innerHTML = '';
    this.rootEl = null;
    this.openMenuId = null;
    this._destroyed = true;
  }

  _escape(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[c]));
  }
}
